import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePortals } from "../context/PortalContext";
import PortalsPanel from "../components/PortalsPanel";
import { APPS, type AppGroup, type Skill } from "../config/tools";
import styles from "./Home.module.css";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const GROUPS: { key: AppGroup; label: string }[] = [
  { key: "configuration", label: "CONFIGURATION" },
  { key: "tools", label: "TOOLS & AGENTS" },
  { key: "admin", label: "ADMIN" },
];

function AppCard({ app }: { app: Skill }) {
  return (
    <Link to={app.route} className={styles.appCard}>
      <svg
        className={styles.appCardIcon}
        width="22"
        height="22"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={app.iconPath} />
      </svg>
      <div className={styles.appCardBody}>
        <span className={styles.appCardName}>{app.name}</span>
        <span className={styles.appCardDesc}>{app.description}</span>
      </div>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const portals = usePortals();
  const greeting = getGreeting();
  const now = useClock();

  const clockTime = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const clockDate = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);

  return (
    <div className={styles.home}>
      <section className={styles.greeting}>
        <div className={styles.greetingText}>
          <h1>
            {greeting}, {user?.name?.split(" ")[0] ?? ""}.
          </h1>
        </div>
        <div className={styles.greetingClock}>
          <span className={styles.clockTime}>{clockTime}</span>
          <span className={styles.clockDate}>{clockDate}</span>
        </div>
      </section>

      {GROUPS.map(({ key, label }) => {
        const apps = APPS.filter((a) => a.group === key);
        if (apps.length === 0) return null;
        return (
          <section key={key} className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionLabel}>{label}</span>
            </div>
            <div className={styles.appsGrid}>
              {apps.map((app) => (
                <AppCard key={app.id} app={app} />
              ))}
            </div>
          </section>
        );
      })}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>CONNECTED PORTALS</span>
          <span className={styles.sectionCount}>{portals.length}</span>
        </div>
        <PortalsPanel />
      </section>
    </div>
  );
}
